import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import { useSettings } from '../context/SettingsContext';

const AIInfo = () => {
  const { nightDiscountPct, nightDiscountEnabled } = useSettings();
  return (
    <div data-testid="ai-info-page" className="pt-28 bg-[#F7F8FA]">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        <h1 className="text-4xl text-[#1A1A2E] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
          Royal Garden Villas — <span className="text-[#F57C20] italic">Complete Information</span>
        </h1>

        {/* Quick Facts */}
        <section className="mb-10">
          <h2 className="text-2xl text-[#1A1A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>At a Glance</h2>
          <div className="bg-white rounded-xl p-6 border border-gray-100 text-sm text-[#4B5563] leading-relaxed space-y-2">
            <p><strong>Property:</strong> Royal Garden Villas — a collection of 7 luxury private pool villas in Lonavala, Maharashtra, India.</p>
            <p><strong>Villa Types:</strong> Individual private 3BHK, 4BHK, 5BHK and Combo Villas of 7BHK (3BHK+4BHK), 8BHK (3BHK+5BHK), 9BHK (4BHK+5BHK) &amp; 12BHK (3BHK+4BHK+5BHK).</p>
            <p><strong>Total Bedrooms:</strong> 12 (across all villas). Each bedroom has an attached washroom with split AC.</p>
            <p><strong>Private Pools:</strong> 3 pools across the compound (each villa has its own pool).</p>
            <p><strong>Max Capacity:</strong> 35 guests (12BHK Celebration Villa).</p>
            <p><strong>Location:</strong> Near Pinewood Park, Inside lane of Lucky Wax Museum, Tata Dam Road, Lonavala 410401. Approx. 5 min from Mapro Gardens.</p>
            <p><strong>Distance from Mumbai:</strong> 83 km (~2 hours via Mumbai-Pune Expressway).</p>
            <p><strong>Distance from Pune:</strong> 64 km (~1.5 hours).</p>
            <p><strong>GPS Coordinates:</strong> 18.764728, 73.435652</p>
            <p><strong>Contact:</strong> +91 98200 88292 | royalgardenlonavala@gmail.com</p>
            <p><strong>Pricing:</strong> Starting Rs.10,999/night (weekday).{nightDiscountEnabled ? ` 1st night at actual rate; ${nightDiscountPct}% off on every night from the 2nd onwards, auto-applied.` : ''}</p>
            <p><strong>Payment:</strong> 100% online only via Razorpay (UPI/Cards/Net Banking). No cash accepted.</p>
            <p><strong>Check-in:</strong> 1:00 PM | <strong>Check-out:</strong> 10:00 AM</p>
            <p><strong>WiFi:</strong> Complimentary free high-speed WiFi in all villas.</p>
            <p><strong>AC:</strong> Split ACs in all bedrooms and living areas. Tower fans provided for double-height living areas.</p>
            <p><strong>Parking:</strong> Dedicated parking per villa &mdash; 2 cars (3/4/5 BHK), 4 cars (7/8/9 BHK), 6 cars (12BHK compound).</p>
            <p><strong>Security:</strong> Gated compound with 24/7 security.</p>
          </div>
        </section>

        {/* Villa Details */}
        <section className="mb-10">
          <h2 className="text-2xl text-[#1A1A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Villa Details & Pricing</h2>
          <div className="space-y-4">
            {[
              { name: "3BHK Artistic Villa - Garden Escape", beds: 3, baths: 3, base: 6, max: 8, weekday: "10,999", weekend: "12,999", deposit: "5,000", desc: "Artistic interiors with double-height ceiling, private pool, outdoor swing. Perfect for intimate family getaways." },
              { name: "4BHK Garden Retreat", beds: 4, baths: 4, base: 8, max: 12, weekday: "13,999", weekend: "15,999", deposit: "7,000", desc: "Spacious 4-bedroom villa with private pool, garden, and modern amenities. Great for birthday parties and family reunions." },
              { name: "5BHK Garden Delight", beds: 5, baths: 5, base: 10, max: 15, weekday: "16,999", weekend: "19,999", deposit: "10,000", desc: "Premium 5-bedroom villa with private pool, hot tub, baby pool, play area. Ideal for large families and celebrations." },
              { name: "12BHK Celebration Villa", beds: 12, baths: 12, base: 24, max: 35, weekday: "41,999", weekend: "47,999", deposit: "20,000", desc: "All 3 villas combined into one grand venue. 3 pools, 3 kitchens, expansive lawns. Perfect for weddings, corporate offsites, and large events." },
            ].map((v, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-gray-100">
                <h3 className="text-[#1A1A2E] font-bold mb-2">{v.name}</h3>
                <p className="text-[#6B7280] text-sm mb-2">{v.desc}</p>
                <div className="text-xs text-[#6B7280] space-y-1">
                  <p>{v.beds} Bedrooms | {v.baths} Washrooms | Base: {v.base} guests | Max: {v.max} guests</p>
                  <p>Weekday: Rs.{v.weekday}/night | Weekend: Rs.{v.weekend}/night | Deposit: Rs.{v.deposit} (refundable)</p>
                  <p>{nightDiscountEnabled ? `Stay Longer, Pay Less: ${nightDiscountPct}% OFF 2nd night onwards (1st night at actual rate) | ` : ''}Extra guests: Rs.1,000/person/night</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Add-on Services */}
        <section className="mb-10">
          <h2 className="text-2xl text-[#1A1A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Add-on Services</h2>
          <div className="bg-white rounded-xl p-6 border border-gray-100 text-sm text-[#4B5563] space-y-2">
            <p><strong>Extra Butler Service (Optional):</strong> Rs.2,000/day (1 PM - 9 PM). Dedicated maid for serving lunch, high tea & dinner. Serving charges only.</p>
            <p><strong>BBQ Service (Optional):</strong> Rs.3,000/meal. Includes Sigdi, Metal Sticks, Charcoal & 1 Labour (2 hours). Additional labour +Rs.1,000/person/meal. Max 2 meals/day. Food not included.</p>
            <p><strong>Bonfire Service (Optional):</strong> Rs.2,000/session. Includes Sigdi and wooden sticks.</p>
            <p><strong>Food Ordering:</strong> Direct ordering by guests from Caterers/Cloud Kitchens (KG basis, free delivery). Swiggy & Zomato available. Kitchen equipped with Crockery, Induction & Microwave.</p>
          </div>
        </section>

        {/* Ideal For */}
        <section className="mb-10">
          <h2 className="text-2xl text-[#1A1A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Ideal For</h2>
          <div className="bg-white rounded-xl p-6 border border-gray-100 text-sm text-[#4B5563]">
            <p>Family holidays, birthday parties, anniversary celebrations, baby showers, kitty parties, family reunions, engagement parties, New Year parties, weekend getaways, corporate offsites, team building events, film & TV shoots, music video production, pre-wedding shoots, weddings, workations.</p>
          </div>
        </section>

        {/* Nearby Attractions */}
        <section className="mb-10">
          <h2 className="text-2xl text-[#1A1A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Nearby Attractions</h2>
          <div className="bg-white rounded-xl p-6 border border-gray-100 text-sm text-[#4B5563] space-y-1">
            <p>Mapro Gardens (5 min) | Tiger Point (5 km) | Bhushi Dam (4 km) | Lonavala Lake (3 km) | Karla Caves (12 km) | Imagica Theme Park (15 km) | Rajmachi Fort (14 km) | Lion's Point (6 km)</p>
          </div>
        </section>

        {/* How to Reach */}
        <section className="mb-10">
          <h2 className="text-2xl text-[#1A1A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>How to Reach</h2>
          <div className="bg-white rounded-xl p-6 border border-gray-100 text-sm text-[#4B5563] space-y-2">
            <p><strong>From Mumbai:</strong> Take Mumbai-Pune Expressway. Exit at Lonavala. ~83 km, 2 hours drive.</p>
            <p><strong>From Pune:</strong> Via Old Mumbai-Pune Highway or Expressway. ~64 km, 1.5 hours.</p>
            <p><strong>By Train:</strong> Lonavala Railway Station (5 km from property).</p>
            <p><strong>Nearest Airports:</strong> Pune Airport (70 km) | Mumbai Airport (95 km).</p>
            <p><strong>Google Maps:</strong> <a href="https://goo.gl/maps/CqXjDkA8P9vdg4tf8" className="text-[#F57C20] underline" target="_blank" rel="noopener noreferrer">Open in Google Maps</a></p>
          </div>
        </section>

        <div className="text-center">
          <Link to="/booking" className="bg-[#F57C20] text-white px-8 py-4 rounded-lg text-sm font-semibold inline-flex items-center gap-2 hover:bg-[#E06A10] transition-all">
            Book Now <FiArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AIInfo;
